import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Close from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { ChangelogIndexEntry } from "src/config/changelog";
import {
  useGetChangelogEntry,
  useGetChangelogIndex,
} from "src/hooks/queries/changelog";
import { useStore } from "src/stores";
import { MarkdownText } from "../ui/MarkdownText";

const CHANGELOG_ARCHIVE_URL =
  "https://docs.formly.ai/changelog/release-notes-webapp";

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  const currentYear = new Date().getFullYear();
  return date.toLocaleDateString("en-US", {
    year: date.getFullYear() === currentYear ? undefined : "numeric",
    month: "short",
    day: "numeric",
  });
}

const ChangelogEntryView = ({ entry }: { entry: ChangelogIndexEntry }) => {
  const { data } = useGetChangelogEntry(entry.file);

  if (!data) return null;

  return (
    <div className="mb-6">
      <MarkdownText>{`**${data.title}**\n\n${data.body}`}</MarkdownText>
    </div>
  );
};

export const ChangelogModal = observer(() => {
  const { uiStore } = useStore();
  const { data: index } = useGetChangelogIndex();
  const theme = useTheme();
  const latestEntry = index?.[0];
  const latestDate = latestEntry?.date;

  useEffect(() => {
    if (!latestDate) return;
    if (uiStore.lastSeenChangelogDate !== latestDate) {
      uiStore.setIsChangelogModalOpen(true);
    }
  }, [latestDate, uiStore]);

  const handleClose = () => {
    uiStore.setIsChangelogModalOpen(false);
    if (latestDate) {
      uiStore.markChangelogSeen(latestDate);
    }
  };

  if (!index || index.length === 0 || !latestEntry) return null;

  return (
    <Dialog
      open={uiStore.isChangelogModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          maxHeight: "80vh",
        },
        // gray-500
        "& .MuiButton-endIcon svg": { fontSize: "16px", color: theme.palette.text.secondary }
      }}

    >
      <DialogTitle
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",

        }}
      >
        <Typography variant="body2" className="text-gray-500">
          {latestDate ? formatDate(latestDate) : ""}
        </Typography>
        <Button
          variant="text"
          color="inherit"
          href={CHANGELOG_ARCHIVE_URL}
          target="_blank"
          endIcon={<ArrowOutwardIcon />}
        >
          Changelog
        </Button>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ justifySelf: "end" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ChangelogEntryView entry={latestEntry} />
      </DialogContent>
    </Dialog>
  );
});
